import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGroups } from '../../hooks/useGroups';
import { fetchGroups } from '../../features/groups/groupsSlice';
import { useTranslation } from 'react-i18next';
import { ACTIONS } from '../../constants/actionTables';
import { requestSearch } from '../../helpers/search';
import ModalAddGroup from '../../components/ModalAddGroup';
import TableComponent from '../../components/Table';
import CustomTabs from '../../components/CustomTabs';
import PageLoader from '../../components/PageLoader';

import { Typography, Chip, Box } from '@mui/material';

import styles from './style.module.css';
import { useAuth } from '../../hooks/useAuth';

function MachinesGroup() {
  const [value, setValue] = useState(0);
  const [dataActive, setDataActive] = useState([]);
  const [dataAchived, setDataAchived] = useState([]);
  const [filteredActiveRows, setFilteredActiveRows] = useState([]);
  const [filteredArchivedRows, setFilteredArchivedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { groups, isLoading } = useGroups();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Load groups data on-demand for this page
  useEffect(() => {
    if (groups.length === 0) {
      dispatch(fetchGroups());
    }
  }, [dispatch, groups.length]);

  const isAdmin = user && user.role === 'admin';

  useEffect(() => {
    const prepare = (group) => {
      const pendingCount = group.pendingApprovalCount || 0;
      return {
        id: group.id,
        image: `${process.env.REACT_APP_BASE_URL}machine-groups/image/${group.image}`,
        number: group.id,
        name: group.name,
        description: group.description,
        machinesCount: group.machines ? group.machines.length : 0,
        ...(isAdmin && {
        pendingApproval: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{pendingCount}</span>
            {pendingCount > 0 && (
              <Chip
                size="small"
                color="warning"
                label={t('approval.pendingApproval')}
              />
            )}
          </Box>
        ),
        }),
      };
    };
    if (groups?.activeMachineGroups) {
      const preparedData = groups.activeMachineGroups.map(prepare);
      setDataActive(preparedData);
      setFilteredActiveRows(preparedData);
    }
    if (groups?.archivedMachineGroups) {
      const preparedData = groups.archivedMachineGroups.map(prepare);
      setDataAchived(preparedData);
      setFilteredArchivedRows(preparedData);
    }
  }, [groups, t, isAdmin]);

  const handleSearch = (searchValue) => {
    if (value === 0) {
      requestSearch(searchValue, dataActive, setFilteredActiveRows);
    } else {
      requestSearch(searchValue, dataAchived, setFilteredArchivedRows);
    }
  };

  const getTotalCount = () => {
    return value === 0 ? dataActive.length : dataAchived.length;
  };

  const handleTabChange = (newValue) => {
    setValue(newValue);
    // Reset search and sorting when switching tabs
    setSortConfig({ key: null, direction: 'asc' });
    if (newValue === 0) {
      setFilteredActiveRows(dataActive);
    } else {
      setFilteredArchivedRows(dataAchived);
    }
  };

  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });

    const dataToSort = value === 0 ? [...filteredActiveRows] : [...filteredArchivedRows];

    dataToSort.sort((a, b) => {
      const aValue = a[columnKey];
      const bValue = b[columnKey];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string') {
        return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    if (value === 0) {
      setFilteredActiveRows(dataToSort);
    } else {
      setFilteredArchivedRows(dataToSort);
    }
  };

  const handleRowClick = (groupId) => {
    navigate(`/machines-groups/${groupId}`);
  };

  const titles = [
    t('generic.image'),
    t('generic.number'),
    t('generic.name'),
    t('generic.description'),
    t('machineGroups.machines'),
    ...(isAdmin ? [t('approval.pendingApproval')] : []),
    t('generic.action'),
  ];
  const columnKeys = [
    'image',
    'number',
    'name',
    'description',
    'machinesCount',
    ...(isAdmin ? ['pendingApproval'] : []),
    'action',
  ];

  return (
    <PageLoader
      loading={isLoading}
      headerTitle={t('generic.machineGroups')}
      headerSubtitle={t('generic.manageMachineGroups')}
    >
      <section className={styles.container}>
        <div className={styles.header}>
          <div className={styles.caption}>
            <Typography variant="h5" fontWeight="bold" className={styles.headerTitle}>
              {t('generic.machineGroups')}
            </Typography>
            <Typography className={styles.headerSubtitle}>
              {t('generic.manageMachineGroups')}
            </Typography>
          </div>
          <div className={styles.addButton}>
            {user && user.role === 'admin' && <ModalAddGroup type={'add'} />}
          </div>
        </div>
        <div className={styles.tabs}>
          <CustomTabs
            tabs={[
              {
                key: 0,
                label: t('machineGroups.activeGroups'),
                onClick: () => handleTabChange(0),
              },
              {
                key: 1,
                label: t('machineGroups.archivedGroups'),
                onClick: () => handleTabChange(1),
              },
            ]}
            activeTab={value}
          />

          {value === 0 && (
            <TableComponent
              titles={titles}
              data={filteredActiveRows}
              action={ACTIONS.ACTIVE_MACHINES_GROUPS}
              search={true}
              showTableHeader={true}
              tableTitle={t('generic.machineGroups')}
              tableCount={`${getTotalCount()} ${t('machineGroups.activeGroups')}`}
              searchPlaceholder={t('generic.search')}
              onSearch={handleSearch}
              onSort={handleSort}
              sortConfig={sortConfig}
              sortableColumns={['number', 'name', 'description']}
              columnKeys={columnKeys}
              onRowClick={handleRowClick}
            />
          )}
          {value === 1 && (
            <TableComponent
              titles={titles}
              data={filteredArchivedRows}
              action={ACTIONS.ARCHIVED_MACHINES_GROUPS}
              search={true}
              showTableHeader={true}
              tableTitle={t('generic.machineGroups')}
              tableCount={`${getTotalCount()} ${t('machineGroups.archivedGroups')}`}
              searchPlaceholder={t('generic.search')}
              onSearch={handleSearch}
              onSort={handleSort}
              sortConfig={sortConfig}
              sortableColumns={['number', 'name', 'description']}
              columnKeys={columnKeys}
              onRowClick={handleRowClick}
            />
          )}
        </div>
      </section>
    </PageLoader>
  );
}

export default MachinesGroup;
